import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl,
} from '@mui/material';

const PLANETS    = ['Sun', 'Moon', 'Mercury', 'Venus', 'Mars', 'Jupiter', 'Saturn', 'Uranus', 'Neptune', 'Pluto', 'Rahu', 'Ketu'];
const POSITION_OPTIONS = ['Ahead of', 'Behind'];
const MOON_STATUS    = ['Weak', 'Strong'];
const MERCURY_STATUS = ['Auspicious', 'Inauspicious'];
const CONJUNCTION_PLANETS = ['Venus', 'Mercury'];
const CONJUNCTION_TYPES   = ['Superior', 'Inferior'];

const initForms = {
  r7: { planet1: 'Mercury', planet2: 'Sun', position: 'Ahead of', value: '2' },
  r8:  { value: '2' },
  r9:  { value: '2' },
  r10: { status: 'Weak',        value: '2' },
  r11: { status: 'Auspicious',  value: '2' },
  r12: { planet: 'Mars', value: '2' },
  r13: { planet: 'Mars', value: '2' },
  r14: { planet: 'Venus', conjType: 'Superior', nDays: '7', windowMode: 'before', value: '2' },
};

const PlanetSelect = ({ value, onChange, exclude }) => (
  <FormControl size="small" sx={{ minWidth: 110 }}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {PLANETS.filter(p => !exclude || !exclude.includes(p)).map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
    </Select>
  </FormControl>
);
PlanetSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, exclude: PropTypes.array };

const Label = ({ children }) => (
  <Typography sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>{children}</Typography>
);
Label.propTypes = { children: PropTypes.node.isRequired };

const NumField = ({ value, onChange }) => (
  <TextField size="small" type="number" value={value} onChange={e => onChange(e.target.value)} sx={{ width: 80 }} />
);
NumField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const AddValueField = ({ value, onChange }) => (
  <TextField
    size="small" type="number" value={value}
    onChange={e => onChange(e.target.value)}
    inputProps={{ min: 1, step: 1 }} sx={{ width: 56 }}
  />
);
AddValueField.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired };

const AddBtn = ({ onClick, disabled }) => (
  <Button
    variant="contained" size="small" onClick={onClick} disabled={disabled}
    sx={{ minWidth: 56, px: 1.5, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
  >
    ADD
  </Button>
);
AddBtn.propTypes = { onClick: PropTypes.func.isRequired, disabled: PropTypes.bool };

const WINDOW_MODE_OPTIONS = [
  { value: 'before', label: 'From specified number of days before the event to the event' },
  { value: 'after',  label: 'From the event to the specified number of days after' },
  { value: 'around', label: 'From specified number of days before the event to the event to after the event' },
];

const EventWindowControls = ({ nDays, windowMode, onNDaysChange, onModeChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
    <TextField size="small" type="number" value={nDays} onChange={e => onNDaysChange(e.target.value)}
      inputProps={{ min: 1, step: 1 }} sx={{ width: 64 }} />
    <Typography sx={{ fontSize: '1rem', whiteSpace: 'nowrap' }}>days</Typography>
    <FormControl size="small" sx={{ minWidth: 420 }}>
      <Select value={windowMode} onChange={e => onModeChange(e.target.value)}>
        {WINDOW_MODE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </Select>
    </FormControl>
  </Box>
);
EventWindowControls.propTypes = {
  nDays: PropTypes.string.isRequired, windowMode: PropTypes.string.isRequired,
  onNDaysChange: PropTypes.func.isRequired, onModeChange: PropTypes.func.isRequired,
};

const CatSelect = ({ value, onChange, options, minWidth }) => (
  <FormControl size="small" sx={{ minWidth: minWidth || 140 }}>
    <Select value={value} onChange={e => onChange(e.target.value)}>
      {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </Select>
  </FormControl>
);
CatSelect.propTypes = { value: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, options: PropTypes.array.isRequired, minWidth: PropTypes.number };

const CriteriaRow = ({ children }) => (
  <Box sx={{
    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1,
    border: '1px solid #d0d0d0', borderRadius: 1, p: 1, mb: 1, backgroundColor: '#fafafa'
  }}>
    {children}
  </Box>
);
CriteriaRow.propTypes = { children: PropTypes.node.isRequired };

const MOAGroup3Tab = ({ onAddCondition, forms: propForms, onFormsChange }) => {
  const [localForms, setLocalForms] = useState(initForms);
  const forms = propForms || localForms;
  const setForms = onFormsChange || setLocalForms;
  const set = (row, field, val) => setForms(f => ({ ...f, [row]: { ...f[row], [field]: val } }));
  const setPair = (row, f1, v, f2) => setForms(prev => {
    const updated = { ...prev[row], [f1]: v };
    if (updated[f2] === v) updated[f2] = PLANETS.find(p => p !== v) || PLANETS[0];
    return { ...prev, [row]: updated };
  });
  const add = (type, condObj) => {
    const val = parseInt(condObj.value, 10);
    if (!val || val < 1) return;
    onAddCondition({ group: 3, type, ...condObj, value: val });
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography fontWeight="bold" fontSize={15} sx={{ textAlign: 'center', mb: 2, letterSpacing: 0.5 }}>
        Esoterica Criteria
      </Typography>

      {/* Row 7: Planet is Ahead of / Behind planet */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r7.planet1} onChange={v => setPair('r7', 'planet1', v, 'planet2')} exclude={[forms.r7.planet2]} />
        <Label>is</Label>
        <CatSelect value={forms.r7.position} onChange={v => set('r7', 'position', v)} options={POSITION_OPTIONS} minWidth={120} />
        <Label>planet</Label>
        <PlanetSelect value={forms.r7.planet2} onChange={v => setPair('r7', 'planet2', v, 'planet1')} exclude={[forms.r7.planet1]} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r7.value} onChange={v => set('r7', 'value', v)} />
        <AddBtn onClick={() => add('vedic_position', { planet1: forms.r7.planet1, planet2: forms.r7.planet2, position: forms.r7.position, value: forms.r7.value })} />
      </CriteriaRow>

      {/* Row 8: All planets between (from) Rahu and Ketu */}
      <CriteriaRow>
        <Label>All planets between (from) Rahu and Ketu</Label>
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r8.value} onChange={v => set('r8', 'value', v)} />
        <AddBtn onClick={() => add('g3_rahu_ketu_between', { value: forms.r8.value })} />
      </CriteriaRow>

      {/* Row 9: All planets between (from) Ketu and Rahu */}
      <CriteriaRow>
        <Label>All planets between (from) Ketu and Rahu</Label>
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r9.value} onChange={v => set('r9', 'value', v)} />
        <AddBtn onClick={() => add('g3_ketu_rahu_between', { value: forms.r9.value })} />
      </CriteriaRow>

      {/* Row 10: Moon Weak/Strong */}
      <CriteriaRow>
        <Label>Moon</Label>
        <CatSelect value={forms.r10.status} onChange={v => set('r10', 'status', v)} options={MOON_STATUS} minWidth={110} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r10.value} onChange={v => set('r10', 'value', v)} />
        <AddBtn onClick={() => add('g3_moon_status', { status: forms.r10.status, value: forms.r10.value })} />
      </CriteriaRow>

      {/* Row 11: Mercury Auspicious/Inauspicious */}
      <CriteriaRow>
        <Label>Mercury</Label>
        <CatSelect value={forms.r11.status} onChange={v => set('r11', 'status', v)} options={MERCURY_STATUS} minWidth={150} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r11.value} onChange={v => set('r11', 'value', v)} />
        <AddBtn onClick={() => add('g3_mercury_status', { status: forms.r11.status, value: forms.r11.value })} />
      </CriteriaRow>

      {/* Row 12: Planet Declination more than that of the Sun */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r12.planet} onChange={v => set('r12', 'planet', v)} exclude={['Sun']} />
        <Label>Declination more than that of the Sun</Label>
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r12.value} onChange={v => set('r12', 'value', v)} />
        <AddBtn onClick={() => add('declination_more_than_sun', { planet: forms.r12.planet, value: forms.r12.value })} />
      </CriteriaRow>

      {/* Row 13: Out Of Bounds – Declination more than 23.45 */}
      <CriteriaRow>
        <Label>Planet</Label>
        <PlanetSelect value={forms.r13.planet} onChange={v => set('r13', 'planet', v)} />
        <Label>Out Of Bounds: Declination more than 23.45</Label>
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r13.value} onChange={v => set('r13', 'value', v)} />
        <AddBtn onClick={() => add('g3_out_of_bounds', { planet: forms.r13.planet, value: forms.r13.value })} />
      </CriteriaRow>

      {/* Row 14: Sun and Planet having Superior/Inferior Conjunction */}
      <CriteriaRow>
        <Label>Sun and</Label>
        <CatSelect value={forms.r14.planet} onChange={v => set('r14', 'planet', v)} options={CONJUNCTION_PLANETS} minWidth={110} />
        <Label>having</Label>
        <CatSelect value={forms.r14.conjType} onChange={v => set('r14', 'conjType', v)} options={CONJUNCTION_TYPES} minWidth={110} />
        <Label>conjunction</Label>
        <EventWindowControls nDays={forms.r14.nDays} windowMode={forms.r14.windowMode}
          onNDaysChange={v => set('r14', 'nDays', v)} onModeChange={v => set('r14', 'windowMode', v)} />
        <Box sx={{ flex: 1 }} />
        <Label>Add value</Label>
        <AddValueField value={forms.r14.value} onChange={v => set('r14', 'value', v)} />
        <AddBtn onClick={() => add('g3_sun_conjunction', { planet: forms.r14.planet, conjType: forms.r14.conjType, nDays: Number(forms.r14.nDays), windowMode: forms.r14.windowMode, value: forms.r14.value })} />
      </CriteriaRow>

    </Box>
  );
};

MOAGroup3Tab.propTypes = {
  onAddCondition: PropTypes.func.isRequired,
  forms: PropTypes.object,
  onFormsChange: PropTypes.func,
};

export default MOAGroup3Tab;
